import { useSyncExternalStore } from "react";

/**
 * The language the app's own words are in: English or Indonesian, this
 * device's choice, kept in localStorage like the theme (theme.ts). Until the
 * user picks, it follows the device: Indonesian when the browser lists
 * Indonesian among its languages, English otherwise. What the bots say is
 * not touched: they answer in whatever language they are written to.
 *
 * Each part of the app keeps its words in a catalog beside it (text/*.ts),
 * one entry per phrase in each language. A phrase with something in it is a
 * function, so each language puts the number or name where its grammar
 * wants it. The Indonesian half is typed from the English one: a phrase
 * missing from it, or one taking other arguments, does not compile.
 */
export type Locale = "en" | "id";

export const LOCALES: ReadonlyArray<{ readonly id: Locale; readonly name: string }> = [
  { id: "en", name: "English" },
  { id: "id", name: "Bahasa Indonesia" },
];

const KEY = "ark-bot.language";

type Phrase = string | ((...args: never[]) => string);

/** One part of the app's words, in both languages. */
export interface Catalog<T> {
  readonly en: T;
  readonly id: T;
}

/** A catalog: the English phrases, and the Indonesian ones, which must match them key for key. */
export function catalog<T extends Record<string, Phrase>>(en: T, id: NoInfer<T>): Catalog<T> {
  return { en, id };
}

function isLocale(value: unknown): value is Locale {
  return value === "en" || value === "id";
}

/** The device's own preference: Indonesian when any of its languages is, else English. */
export function deviceLocale(languages: readonly string[] | undefined = globalThis.navigator?.languages): Locale {
  return (languages ?? []).some((tag) => /^(id|in)(-|$)/i.test(tag)) ? "id" : "en";
}

/** The stored choice, or the device's when there is none (or it is not one of ours). */
export function readLocale(storage: Pick<Storage, "getItem"> | undefined = globalThis.localStorage, languages?: readonly string[]): Locale {
  let stored: string | null = null;
  try {
    stored = storage?.getItem(KEY) ?? null;
  } catch {
    // Storage refused (a private window): the device decides.
  }
  return isLocale(stored) ? stored : deviceLocale(languages);
}

const listeners = new Set<() => void>();
let current: Locale = typeof document === "undefined" ? "en" : readLocale();

function mark(locale: Locale): void {
  if (typeof document !== "undefined") document.documentElement.lang = locale;
}
mark(current);

/** The language the app is in now, for code outside a component. */
export function locale(): Locale {
  return current;
}

/** Picks the language for this device: saved, applied at once, and every reader told. */
export function setLocale(next: Locale): void {
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // Storage refused: the choice holds until the page is closed.
  }
  current = next;
  mark(next);
  for (const listener of listeners) listener();
}

/** Tests only: read the stored choice again. */
export function reloadLocale(storage?: Pick<Storage, "getItem">, languages?: readonly string[]): void {
  current = readLocale(storage, languages);
  for (const listener of listeners) listener();
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export function useLocale(): Locale {
  return useSyncExternalStore(subscribe, () => current);
}

/** A catalog's phrases in the language the app is in now, for code outside a component. */
export function text<T>(words: Catalog<T>): T {
  return words[current];
}

/** A catalog's phrases in the app's language, in a component: it draws again when the language changes. */
export function useText<T>(words: Catalog<T>): T {
  return words[useLocale()];
}

/**
 * The locale to hand Intl for dates, times and numbers: Indonesian's own in
 * Indonesian, and in English whatever the code used before there was a
 * choice (a fixed "en-GB", or undefined for the device's).
 */
export function formatLocale(english?: string): string | undefined {
  return current === "id" ? "id-ID" : english;
}
